/**
 * /messages — 1-on-1 inbox. Two-pane layout:
 *   - Left: list of conversations (last message + unread count + counterpart avatar)
 *   - Right: selected conversation with input to send a new message
 *
 * You can only message users you're connected with. Reading a disconnected
 * thread is allowed (data preservation) but sending is blocked.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, Loader2, MessageCircle, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("to") || null;

  const load = () => {
    setLoading(true);
    http.get("/messages/threads").then((r) => setThreads(r.data.threads || [])).finally(() => setLoading(false));
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) {
    return <div className="max-w-4xl mx-auto px-6 py-16 text-center" style={{ color: "var(--wofy-text-soft)" }}>Please sign in to view your messages.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="messages-page">
      <h1 className="font-display text-4xl mb-6">Messages</h1>
      <div className="grid md:grid-cols-[320px_1fr] gap-0 border rounded-2xl overflow-hidden" style={{ borderColor: "var(--wofy-border)", minHeight: 500 }}>
        {/* Threads list */}
        <div className={`border-r overflow-y-auto ${selectedId ? "hidden md:block" : ""}`} style={{ borderColor: "var(--wofy-border)", maxHeight: 700 }}>
          <div className="p-4 border-b text-xs font-mono uppercase tracking-widest" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
            Conversations {threads.length > 0 && <span className="opacity-60">({threads.length})</span>}
          </div>
          {loading ? (
            <div className="p-6 text-sm inline-flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : threads.length === 0 ? (
            <div className="p-6 text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>
              <MessageCircle className="w-6 h-6 mx-auto mb-2 opacity-40" />
              No conversations yet. <br /> Connect with someone and start a chat.
              <Link to="/members" className="wofy-btn-primary text-xs mt-4 inline-flex">Browse members</Link>
            </div>
          ) : (
            threads.map((t) => (
              <button
                key={t.user.id}
                onClick={() => setParams({ to: t.user.id })}
                className={`w-full flex gap-3 px-4 py-3 border-b hover:bg-black/5 text-left transition-colors ${selectedId === t.user.id ? "bg-black/5" : ""}`}
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`thread-${t.user.handle || t.user.id}`}
              >
                <Avatar src={t.user.avatar_url || t.user.profile_picture} name={t.user.name} gender={t.user.gender} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium truncate">{t.user.name || "Member"}</div>
                    <time className="text-[10px] font-mono opacity-60">{fmt(t.last?.created_at)}</time>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs truncate flex-1" style={{ color: "var(--wofy-text-soft)" }}>
                      {t.last?.from_user_id === user.id && <span className="opacity-60">You: </span>}
                      {t.last?.body}
                    </div>
                    {t.unread > 0 && (
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "var(--wofy-primary)", color: "#FDFBF7" }}>{t.unread}</span>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Thread view */}
        <div className={`flex flex-col ${!selectedId ? "hidden md:flex md:items-center md:justify-center" : ""}`} style={{ minHeight: 500 }}>
          {selectedId ? (
            <Thread otherUserId={selectedId} onBack={() => setParams({})} onSent={load} />
          ) : (
            <div className="text-sm opacity-60">Select a conversation to start reading.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Thread({ otherUserId, onBack, onSent }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef();

  const load = () => {
    setLoading(true);
    http.get(`/messages/thread/${otherUserId}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ messages: [], user: null, connected: false }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    http.post(`/messages/thread/${otherUserId}/read`).catch(() => {});
  }, [otherUserId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await http.post("/messages/send", { to_user_id: otherUserId, body: text.trim() });
      setText("");
      load();
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send.");
    } finally { setSending(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const other = data?.user || {};

  return (
    <>
      <div className="p-3 border-b flex items-center gap-3" style={{ borderColor: "var(--wofy-border)" }}>
        <button onClick={onBack} className="md:hidden opacity-60 hover:opacity-100" aria-label="Back"><ChevronLeft className="w-5 h-5" /></button>
        <Avatar src={other.avatar_url || other.profile_picture} name={other.name} gender={other.gender} size={36} />
        <div className="flex-1 min-w-0">
          <Link to={other.handle ? `/u/${other.handle}` : "#"} className="font-medium truncate hover:underline">{other.name || "Member"}</Link>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{ maxHeight: 550 }}>
        {(data?.messages || []).map((m) => {
          const mine = m.from_user_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className="max-w-[75%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap"
                style={mine
                  ? { background: "var(--wofy-primary)", color: "#FDFBF7", borderBottomRightRadius: 4 }
                  : { background: "var(--wofy-surface)", borderBottomLeftRadius: 4 }
                }
              >
                {m.body}
                <div className="text-[10px] font-mono mt-1 opacity-60">{fmt(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      {data?.connected === false ? (
        <div className="p-4 text-xs text-center opacity-60 border-t" style={{ borderColor: "var(--wofy-border)" }}>
          You aren't connected — sending is disabled.
        </div>
      ) : (
        <div className="p-3 border-t flex gap-2" style={{ borderColor: "var(--wofy-border)" }}>
          <input
            className="wofy-input flex-1"
            placeholder="Write a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
            data-testid="message-input"
            maxLength={4000}
          />
          <button onClick={send} disabled={sending || !text.trim()} className="wofy-btn-primary text-sm" data-testid="message-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      )}
    </>
  );
}

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  } catch { return ""; }
}
